import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Loader2, AlertCircle, TrendingUp, TrendingDown, Target,
  Sparkles, ArrowRightLeft, History, Lock, Search,
} from 'lucide-react';
import ModalCloseButton from './ModalCloseButton';
import { getRecentPairs, rememberPair } from '@/lib/compareThesesHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CompareThesesModal
 * --------------------
 * Side-by-side LLM Executive Summary briefs for two tickers. Mirrors
 * the regular Compare Stocks modal UX:
 *
 *   • Two ticker inputs (or one locked + one editable when launched
 *     from a Triangle context — `lockedTicker` prop).
 *   • Stat strip at the top of each column (Score · Verdict · Yield
 *     · Chowder · Buy Checklist pass count) for at-a-glance fundies.
 *   • Full LLM brief below: headline → executive summary → bull case
 *     → bear case → what changes our mind.
 *   • Both columns show a spinning loader + "Please wait, creating
 *     theses now…" until their respective fetch resolves. Each side
 *     clears its loader independently.
 *   • Last 5 ticker pairs saved in localStorage; click a Recent chip
 *     to reload that pair. The backend's 24h cache handles whether
 *     the briefs are instant or freshly generated.
 *
 * Reused from: Watchlist Summary, Portfolio Toolbox (free mode);
 * Triangle Scorecard, Standalone Triangle page (locked mode).
 *
 * Round 95al (Jun 2026).
 */

// chipTint — derive a small color hint for the "Recent" pair chip
// from each side's last-known verdict + score. Pure derivation, no
// extra LLM cost. Tier:
//   • rose    — either side was an "Educational NOT TO BUY"
//   • emerald — both sides scored 60+ AND neither was NOT TO BUY
//   • amber   — one side healthy + one neutral / partial data
//   • muted   — no verdict data yet (older entry pre-upgrade)
function chipTint(pair) {
  const vA = pair?.verdictA;
  const vB = pair?.verdictB;
  const sA = typeof pair?.scoreA === 'number' ? pair.scoreA : null;
  const sB = typeof pair?.scoreB === 'number' ? pair.scoreB : null;
  if (!vA && !vB && sA == null && sB == null) {
    return 'bg-muted/60 hover:bg-muted border-border hover:border-violet-400';
  }
  if (vA === 'Educational NOT TO BUY' || vB === 'Educational NOT TO BUY') {
    return 'bg-rose-100 dark:bg-rose-950/40 border-rose-300/60 hover:bg-rose-200 dark:hover:bg-rose-950/60 hover:border-rose-400 text-rose-700 dark:text-rose-200';
  }
  if (sA != null && sB != null && sA >= 60 && sB >= 60) {
    return 'bg-emerald-100 dark:bg-emerald-950/40 border-emerald-300/60 hover:bg-emerald-200 dark:hover:bg-emerald-950/60 hover:border-emerald-400 text-emerald-700 dark:text-emerald-200';
  }
  return 'bg-amber-100 dark:bg-amber-950/40 border-amber-300/60 hover:bg-amber-200 dark:hover:bg-amber-950/60 hover:border-amber-400 text-amber-800 dark:text-amber-200';
}

function chipTooltip(pair) {
  if (!pair) return '';
  const parts = [`Reload ${pair.a} vs ${pair.b}`];
  if (pair.verdictA || pair.verdictB) {
    const a = pair.verdictA ? `${pair.a}: ${pair.verdictA.replace('Educational ', '')}` : null;
    const b = pair.verdictB ? `${pair.b}: ${pair.verdictB.replace('Educational ', '')}` : null;
    if (a) parts.push(a);
    if (b) parts.push(b);
  }
  return parts.join(' · ');
}

function TickerBox({ value, onChange, placeholder, locked, testid }) {
  if (locked) {
    return (
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border bg-muted/50 text-sm font-mono font-bold uppercase tracking-wider"
        data-testid={testid}
      >
        <Lock className="w-3 h-3 text-muted-foreground" />
        <span>{value || '—'}</span>
      </div>
    );
  }
  return (
    <TickerSearchBox value={value} onChange={onChange} placeholder={placeholder} testid={testid} />
  );
}

/**
 * TickerSearchBox — debounced search-as-you-type picker, modeled on
 * the regular Compare Stocks modal's SearchBox. Hits
 * `/api/stocks/search?q=…&limit=8` and shows ticker · name · yield
 * for each result. Clicking a row commits the ticker (we call
 * onChange with the picked symbol). Free-typed text is still accepted
 * — pressing Compare runs whatever's in the box.
 */
function TickerSearchBox({ value, onChange, placeholder, testid }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  // Trigger a debounced search from the change handler (not an
  // effect) — keeps lint happy and the data flow obvious.
  const launchSearch = useCallback((nextQ) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!nextQ || !nextQ.trim()) {
      setResults([]);
      setBusy(false);
      return;
    }
    setBusy(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/stocks/search`, {
          params: { q: nextQ.trim(), limit: 8 },
        });
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 200);
  }, []);

  useEffect(() => {
    function onDocClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const commit = (ticker) => {
    const T = String(ticker || '').toUpperCase().trim();
    onChange(T);
    setOpen(false);
    setResults([]);
  };

  const q = value || '';

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border bg-background focus-within:ring-2 focus-within:ring-violet-500/40 w-48 sm:w-56">
        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={q}
          placeholder={placeholder}
          onChange={(e) => {
            // Allow letters, digits, spaces, periods, hyphens, ampersands —
            // so users can type either a ticker ("KO") or a name fragment
            // ("coca", "pepsi", "jpmorgan"). Capped at 24 chars. Server
            // searches both ticker and name fields.
            const next = e.target.value.toUpperCase().replace(/[^A-Z0-9 .\-&]/g, '').slice(0, 24);
            onChange(next);
            setOpen(true);
            launchSearch(next);
          }}
          onFocus={() => { setOpen(true); if (q) launchSearch(q); }}
          className="flex-1 min-w-0 bg-transparent text-sm font-mono font-bold uppercase tracking-wider outline-none placeholder:text-muted-foreground placeholder:font-normal placeholder:normal-case"
          data-testid={testid}
        />
        {busy && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground shrink-0" />}
      </div>

      {open && q.trim().length > 0 && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-lg border bg-popover shadow-lg min-w-[220px]"
          data-testid={`${testid}-results`}
        >
          {results.length === 0 && !busy && (
            <div className="px-3 py-2 text-[11px] text-muted-foreground">No matches.</div>
          )}
          {results.map((s) => (
            <button
              key={s.ticker}
              type="button"
              onClick={() => commit(s.ticker)}
              className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left hover:bg-muted transition-colors border-b border-border/40 last:border-b-0"
              data-testid={`${testid}-result-${s.ticker}`}
            >
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-bold font-mono">{s.ticker}</span>
                <span className="truncate text-[10px] text-muted-foreground max-w-[140px]">{s.name}</span>
              </div>
              <span className="shrink-0 text-xs font-bold font-mono text-emerald-600 dark:text-emerald-400">
                {typeof s.dividend_yield === 'number' ? `${s.dividend_yield.toFixed(2)}%` : '—'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatStrip({ data }) {
  if (!data) return null;
  const verdictTone = {
    'Educational BUY': 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-300/60',
    'Educational NOT TO BUY': 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-300/60',
    'Educational NEUTRAL': 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-300/60',
    'Educational Signal': 'bg-muted/60 text-muted-foreground border-border',
  }[data.verdict_label] || 'bg-muted/60 text-muted-foreground border-border';
  const chowderTone = {
    pass: 'text-emerald-600 dark:text-emerald-400',
    weak: 'text-amber-600 dark:text-amber-400',
    fail: 'text-rose-600 dark:text-rose-400',
  }[data.chowder_status] || 'text-muted-foreground';
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-bold pb-2 mb-2 border-b border-border/60" data-testid="theses-stat-strip">
      <span className="px-1.5 py-0.5 rounded-full bg-muted/60 border border-border">
        Score {data.score ?? '—'}/100
      </span>
      <span className={`px-1.5 py-0.5 rounded-full border ${verdictTone}`}>
        {data.verdict_label}
      </span>
      {typeof data.yield_pct === 'number' && (
        <span className="px-1.5 py-0.5 rounded-full bg-muted/60 border border-border">
          Yield {data.yield_pct.toFixed(2)}%
        </span>
      )}
      {typeof data.chowder_score === 'number' && (
        <span className={`px-1.5 py-0.5 rounded-full bg-muted/60 border border-border ${chowderTone}`}>
          Chowder {data.chowder_score.toFixed(1)}
          {typeof data.chowder_threshold === 'number' && (
            <span className="opacity-70"> (≥{data.chowder_threshold.toFixed(0)})</span>
          )}
        </span>
      )}
      <span className="px-1.5 py-0.5 rounded-full bg-muted/60 border border-border">
        Checklist {data.checks_passed}/{data.checks_total}
      </span>
    </div>
  );
}

function ThesisColumn({ ticker, data, loading, err, sideLabel }) {
  return (
    <div
      className="flex flex-col rounded-lg border bg-card overflow-hidden min-h-[400px]"
      data-testid={`thesis-column-${sideLabel}`}
    >
      <div className="px-3 py-2 bg-muted/40 border-b flex items-center gap-2 sticky top-0 z-[1]">
        <Sparkles className="w-3.5 h-3.5 text-violet-500 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            DiviDrip Thesis · {sideLabel === 'a' ? 'Stock A' : 'Stock B'}
          </div>
          <div className="text-sm font-mono font-bold text-yellow-600 dark:text-yellow-400 truncate">
            {ticker || '—'}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {!ticker && (
          <p className="text-xs text-muted-foreground text-center mt-8">
            Enter a ticker to load the thesis.
          </p>
        )}

        {ticker && loading && (
          <div className="flex flex-col items-center justify-center text-center py-12 gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            <p className="text-xs font-bold text-muted-foreground">
              Please wait, creating theses now…
            </p>
            <p className="text-[10px] text-muted-foreground/70 max-w-[200px]">
              Usually under 5 seconds. Up to 12 seconds for tickers we haven&apos;t seen recently.
            </p>
          </div>
        )}

        {ticker && err && !loading && (
          <div className="flex items-start gap-2 rounded-md border border-rose-300/60 bg-rose-50/60 dark:bg-rose-950/30 px-3 py-2 text-xs" data-testid={`thesis-err-${sideLabel}`}>
            <AlertCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
            <span className="font-bold">{err}</span>
          </div>
        )}

        {ticker && data && !loading && (
          <>
            <StatStrip data={data} />
            {data.headline && (
              <p className="text-xs font-bold leading-snug text-foreground/90">{data.headline}</p>
            )}
            <section data-testid={`thesis-exec-${sideLabel}`}>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-1">
                Executive Summary
              </h4>
              <p className="text-xs font-bold leading-relaxed whitespace-pre-line">
                {data.sections?.executive_summary || ''}
              </p>
            </section>

            {Array.isArray(data.sections?.bull_case) && data.sections.bull_case.length > 0 && (
              <section className="rounded-md border border-emerald-300/60 bg-emerald-50/60 dark:bg-emerald-950/30 p-2" data-testid={`thesis-bull-${sideLabel}`}>
                <h4 className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-300 mb-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Bull Case
                </h4>
                <ul className="space-y-0.5 text-[11px] font-bold">
                  {data.sections.bull_case.map((b, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">▲</span>
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {Array.isArray(data.sections?.bear_case) && data.sections.bear_case.length > 0 && (
              <section className="rounded-md border border-rose-300/60 bg-rose-50/60 dark:bg-rose-950/30 p-2" data-testid={`thesis-bear-${sideLabel}`}>
                <h4 className="text-[10px] font-bold uppercase tracking-[0.12em] text-rose-700 dark:text-rose-300 mb-1 flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" /> Bear Case
                </h4>
                <ul className="space-y-0.5 text-[11px] font-bold">
                  {data.sections.bear_case.map((b, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-rose-600 dark:text-rose-400 mt-0.5">▼</span>
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {Array.isArray(data.sections?.what_changes_our_mind) && data.sections.what_changes_our_mind.length > 0 && (
              <section className="rounded-md border border-violet-300/60 bg-violet-50/60 dark:bg-violet-950/30 p-2" data-testid={`thesis-changes-${sideLabel}`}>
                <h4 className="text-[10px] font-bold uppercase tracking-[0.12em] text-violet-700 dark:text-violet-300 mb-1 flex items-center gap-1">
                  <Target className="w-3 h-3" /> What Changes Our Mind
                </h4>
                <ul className="space-y-0.5 text-[11px] font-bold">
                  {data.sections.what_changes_our_mind.map((b, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-violet-600 dark:text-violet-400 mt-0.5">→</span>
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.data_source === 'fallback' && (
              <p className="text-[10px] text-muted-foreground italic text-center">
                LLM service unavailable — showing rule-based fallback.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function CompareThesesModal({
  onClose,
  lockedTicker = null,
  initialA = '',
  initialB = '',
}) {
  // This modal is always mounted-on-demand by the parent (see
  // `CompareThesesButton.jsx` / `Dashboard.js` — they conditionally
  // render this component with `{open && <CompareThesesModal …/>}`).
  // That means every "open" gives us a fresh mount, so initial state
  // can come straight from props — no reset-on-open useEffect needed.
  const [tickerA, setTickerA] = useState(lockedTicker || initialA || '');
  const [tickerB, setTickerB] = useState(initialB || '');
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [errA, setErrA] = useState(null);
  const [errB, setErrB] = useState(null);
  const [recent, setRecent] = useState(() => getRecentPairs());
  const [submitted, setSubmitted] = useState(false);
  // Auto-unlock Stock A once a comparison has fully loaded — lets the
  // user swap Stock A on the fly without closing the modal. Resets to
  // locked when the modal unmounts (X click, escape, page navigation).
  const [unlockedA, setUnlockedA] = useState(false);
  const abortRef = useRef({ a: 0, b: 0 });
  // Track the latest resolved data for each side so we can fire the
  // "upgrade saved pair with verdict + score" persist call as soon as
  // both sides have resolved — without putting setState inside an
  // effect (lints clean and re-renders cheap).
  const dataARef = useRef(null);
  const dataBRef = useRef(null);

  // Lock body scroll while mounted.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const fetchSide = useCallback(async (side, ticker) => {
    const T = String(ticker || '').toUpperCase().trim();
    if (!T) return;
    const myToken = ++abortRef.current[side];
    if (side === 'a') { setLoadingA(true); setErrA(null); setDataA(null); dataARef.current = null; }
    else              { setLoadingB(true); setErrB(null); setDataB(null); dataBRef.current = null; }
    try {
      const r = await axios.get(`${API}/stocks/${encodeURIComponent(T)}/executive-summary`, {
        withCredentials: true,
      });
      // If a newer fetch superseded us, drop this result.
      if (abortRef.current[side] !== myToken) return;
      const result = r?.data || null;
      if (side === 'a') { setDataA(result); dataARef.current = result; }
      else              { setDataB(result); dataBRef.current = result; }
      // Both sides resolved? Upgrade the saved pair entry with each
      // side's verdict + score so future opens can tint the Recent
      // chip — zero extra LLM cost since we already have the data.
      // Also unlock Stock A so the user can swap it without closing.
      const A = dataARef.current;
      const B = dataBRef.current;
      if (A && B) {
        setRecent(rememberPair(A.ticker, B.ticker, {
          verdictA: A.verdict_label,
          verdictB: B.verdict_label,
          scoreA: A.score,
          scoreB: B.score,
        }));
        if (lockedTicker) setUnlockedA(true);
      }
    } catch (e) {
      if (abortRef.current[side] !== myToken) return;
      const msg = e?.response?.status === 404
        ? `${T}: not found in our database.`
        : e?.message || 'Failed to load thesis';
      if (side === 'a') setErrA(msg);
      else              setErrB(msg);
    } finally {
      if (abortRef.current[side] !== myToken) return;
      if (side === 'a') setLoadingA(false);
      else              setLoadingB(false);
    }
  }, []);

  const runCompare = useCallback(() => {
    const A = String(tickerA || '').toUpperCase().trim();
    const B = String(tickerB || '').toUpperCase().trim();
    if (!A || !B) return;
    if (A === B) {
      setErrB('Pick a different ticker than Stock A.');
      return;
    }
    setSubmitted(true);
    fetchSide('a', A);
    fetchSide('b', B);
    setRecent(rememberPair(A, B));
  }, [tickerA, tickerB, fetchSide]);

  const loadRecent = useCallback((pair) => {
    if (lockedTicker && !unlockedA) {
      // Still locked — only the 2nd slot changes; pick whichever
      // side of the recent pair isn't the locked ticker.
      setTickerB(pair.b === lockedTicker ? pair.a : pair.b);
    } else {
      setTickerA(pair.a);
      setTickerB(pair.b);
    }
  }, [lockedTicker, unlockedA]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4"
      onClick={onClose}
      data-testid="compare-theses-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Compare Theses"
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-6xl max-h-[94vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="compare-theses-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2 p-3 sm:p-4 border-b shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <ArrowRightLeft className="w-5 h-5 text-violet-500 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-bold leading-tight">Compare Theses</h2>
              <p className="text-[11px] font-bold text-foreground/80 leading-snug">
                {lockedTicker && !unlockedA
                  ? `Stock A is locked to ${lockedTicker}. Pick a 2nd ticker to compare.`
                  : lockedTicker && unlockedA
                    ? `Stock A unlocked — swap either side freely. Close the modal to reset to ${lockedTicker}.`
                    : 'Pick any two tickers — we generate the Bull/Bear thesis for both. Search by ticker or company name (e.g. "coca" → KO, "pepsi" → PEP).'}
              </p>
              {recent.length > 0 && (
                <p className="text-[10px] font-bold text-foreground/75 leading-snug mt-0.5">
                  Recent chips: <span className="text-emerald-600 dark:text-emerald-400">green</span> = both scored 60+ ·{' '}
                  <span className="text-amber-700 dark:text-amber-300">amber</span> = mixed ·{' '}
                  <span className="text-rose-600 dark:text-rose-400">rose</span> = either was NOT TO BUY ·{' '}
                  <span className="font-mono">Δ</span> = Stock A score minus Stock B score.
                </p>
              )}
            </div>
          </div>
          <ModalCloseButton onClick={onClose} testId="compare-theses-close" />
        </div>

        {/* Picker row */}
        <div className="px-3 sm:px-4 py-3 border-b shrink-0 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Stock A</span>
            <TickerBox
              value={tickerA}
              onChange={setTickerA}
              placeholder="KO"
              locked={!!lockedTicker && !unlockedA}
              testid="compare-theses-input-a"
            />
            {lockedTicker && unlockedA && (
              <span
                className="text-[9px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-300"
                data-testid="compare-theses-stock-a-unlocked"
                title={`Stock A was unlocked from "${lockedTicker}" after the first comparison. Close the modal to reset to locked.`}
              >
                unlocked
              </span>
            )}
          </div>
          <span className="text-muted-foreground">vs</span>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Stock B</span>
            <TickerBox
              value={tickerB}
              onChange={setTickerB}
              placeholder="PEP"
              locked={false}
              testid="compare-theses-input-b"
            />
          </div>
          <button
            type="button"
            onClick={runCompare}
            disabled={!tickerA || !tickerB || loadingA || loadingB}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-violet-600 hover:bg-violet-700 active:bg-violet-800 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="compare-theses-run-btn"
          >
            {(loadingA || loadingB) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Compare
          </button>
        </div>

        {/* Recent pairs */}
        {recent.length > 0 && (
          <div className="px-3 sm:px-4 py-2 border-b shrink-0 flex flex-wrap items-center gap-1.5" data-testid="compare-theses-recent">
            <History className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mr-1">Recent</span>
            {recent.map((p, i) => {
              const disabled = !!(lockedTicker && !unlockedA && p.a !== lockedTicker && p.b !== lockedTicker);
              const tint = chipTint(p);
              const delta = (typeof p.scoreA === 'number' && typeof p.scoreB === 'number')
                ? p.scoreA - p.scoreB
                : null;
              return (
                <button
                  key={`${p.a}-${p.b}-${i}`}
                  type="button"
                  onClick={() => loadRecent(p)}
                  disabled={disabled}
                  title={disabled
                    ? 'Stock A is locked — only pairs containing this ticker can be reloaded.'
                    : chipTooltip(p)}
                  className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 ${tint}`}
                  data-testid={`compare-theses-recent-${i}`}
                >
                  <span>{p.a} · {p.b}</span>
                  {delta !== null && (
                    <span
                      className="px-1 py-0 rounded-sm bg-black/10 dark:bg-white/10 text-[9px] font-mono font-bold"
                      data-testid={`compare-theses-recent-${i}-delta`}
                    >
                      Δ{delta > 0 ? '+' : ''}{delta}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Two columns */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 pb-24 sm:pb-4">
          {!submitted && (
            <div className="text-center py-16 text-sm text-muted-foreground">
              <ArrowRightLeft className="w-6 h-6 mx-auto mb-2 opacity-50" />
              Pick two tickers and tap Compare to see their theses side-by-side.
            </div>
          )}
          {submitted && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ThesisColumn
                ticker={tickerA}
                data={dataA}
                loading={loadingA}
                err={errA}
                sideLabel="a"
              />
              <ThesisColumn
                ticker={tickerB}
                data={dataB}
                loading={loadingB}
                err={errB}
                sideLabel="b"
              />
            </div>
          )}
        </div>

        {/* Footer disclaimer */}
        <div className="px-3 sm:px-4 py-2 border-t shrink-0 text-[11px] font-bold text-amber-900 dark:text-amber-200 bg-yellow-100/60 dark:bg-yellow-900/30 flex items-start gap-1.5">
          <span>⚠</span>
          <span>
            <strong>Educational research starter — not financial advice.</strong> Theses are generated from public financials and DiviDrip&apos;s Buy Checklist signals. Always do your own research before allocating capital.
          </span>
        </div>
      </div>
    </div>
  );
}
